// Mosaic — a modal overlay that tiles selected Clips/photos into one MP4.
//
// A thin React frame over the framework-agnostic Mosaic cores (src/cores/mosaic.ts
// + buildMosaic.ts): it takes the picked selection (clips animate, photos are
// static cells), shows a live grid PREVIEW you can REORDER (drag or arrow keys), pick a
// Square/Vertical preset for, fine-tune the column count of (e.g. 12 cells → 3×4), and
// set the LENGTH of (whole loops of the longest clip, defaulting to the count nearest
// 30 s — Instagram takes an MP4 where it refuses a GIF, so even one photo leaves here
// as a postable video), then encodes the grid to an MP4 via WebCodecs
// (mediabunny) with a Cancel that aborts the encode, previews + downloads it, and
// reports any tiles that couldn't be read ("Tiled N of M").
//
// Each picked clip is decoded ONCE on open (the length needs the longest loop) and its
// frames are handed to the encode, so Encode never re-hits the short-TTL URL (#138).
//
// WebCodecs is browser-only; under jsdom / a browser without VideoEncoder the
// builder rejects with a clear message and the overlay shows it (graceful
// degradation), so this component is exercised in tests with a stubbed builder.

import { useCallback, useEffect, useRef, useState } from "react";
import type { MediaItem } from "../api";
import {
  clipDuration,
  isClip,
  loopsForTarget,
  maxLoops,
  mosaicDuration,
  planGrid,
} from "../cores";
import {
  buildMosaicFromSelection,
  decodeClipFromUrl,
  mosaicFilename,
  type BuildMosaicOptions,
  type GifFrame,
  type MosaicLayout,
  type MosaicSelectionItem,
} from "../cores";
import { mediaLabel, uniqueDownloadName } from "../download";
import { Modal } from "./Modal";

export interface MosaicProps {
  /** The picked media, in pick order (= cell order). */
  items: MediaItem[];
  /** Close the overlay. */
  onClose: () => void;
  /** Reorder cell `from` → `to` (drives the selection, so the gallery badges stay in sync). */
  onReorder?: (from: number, to: number) => void;
  /**
   * Injectable Mosaic builder (tests stub it — WebCodecs is browser-only); the
   * default runs the real cores flow (fetch → decode → WebCodecs encode → MP4).
   */
  buildMosaic?: (
    items: MosaicSelectionItem[],
    options: BuildMosaicOptions,
  ) => Promise<Blob>;
  /**
   * Injectable clip decoder (tests stub it; MediaGrid passes the Sheet's). Each picked
   * Clip is decoded ONCE on open so the Length stepper knows the longest loop, and the
   * frames go to the builder so Encode never re-fetches the short-TTL URL (#138).
   */
  decodeClip?: (url: string) => Promise<readonly GifFrame[]>;
}

type Phase = "idle" | "loading" | "encoding" | "ready" | "error";

const LAYOUTS: { value: MosaicLayout; label: string }[] = [
  { value: "square", label: "Square (1:1)" },
  { value: "vertical", label: "Vertical (Stories)" },
];

/** Seconds to one decimal, trimmed ("30 s", "29.4 s") — how the length stepper reads. */
function formatSeconds(seconds: number): string {
  return `${Math.round(seconds * 10) / 10} s`;
}

export function Mosaic({
  items,
  onClose,
  onReorder,
  buildMosaic = buildMosaicFromSelection,
  decodeClip = decodeClipFromUrl,
}: MosaicProps): React.JSX.Element {
  const [layout, setLayout] = useState<MosaicLayout>("square");
  // Explicit grid width from the stepper. `null` = follow the active layout preset's
  // natural width; a number overrides it (clamped to the cell count in planGrid).
  const [cols, setCols] = useState<number | null>(null);
  // Output length from its stepper, in whole loops of the base (the longest clip, or the
  // 3 s still). `null` = the default, the loop count nearest 30 s (loopsForTarget).
  const [loops, setLoops] = useState<number | null>(null);
  // Each picked Clip's decoded frames by key; `null` once a decode failed. Photos never
  // appear here (a static cell has no length of its own).
  const [clipFrames, setClipFrames] = useState<
    ReadonlyMap<string, readonly GifFrame[] | null>
  >(() => new Map());
  const [phase, setPhase] = useState<Phase>("idle");
  const [percent, setPercent] = useState(0);
  const [mp4Url, setMp4Url] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Set when some tiles were silently skipped (N tiled of M requested). */
  const [tiled, setTiled] = useState<{ n: number; of: number } | null>(null);
  /** The cell index currently being dragged (for the drop target + a visual cue). */
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  /** Polite announcer — a reorder / completion isn't otherwise re-read by a screen reader. */
  const [liveMsg, setLiveMsg] = useState("");

  // The current object URL, revoked when replaced/unmounted (no leaks).
  const urlRef = useRef<string | null>(null);
  const setObjectUrl = useCallback((url: string | null) => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = url;
    setMp4Url(url);
  }, []);
  useEffect(() => () => setObjectUrl(null), [setObjectUrl]);

  // Abort handle for the running encode (Cancel).
  const abortRef = useRef<AbortController | null>(null);
  // Focus target on completion (the Cancel button it replaces just unmounted).
  const downloadRef = useRef<HTMLAnchorElement>(null);

  const selectionCount = items.length;
  const busy = phase === "loading" || phase === "encoding";
  // The most columns that make sense: one per cell (a single row). `null` cols falls
  // back to the layout preset's natural width; otherwise clamp the stepper's value.
  const maxCols = Math.max(1, selectionCount);
  const autoCols = planGrid(maxCols, layout).cols;
  const effectiveCols = cols == null ? autoCols : Math.min(maxCols, Math.max(1, cols));
  const grid = planGrid(maxCols, layout, effectiveCols);
  const slotCount = grid.cols * grid.rows;

  // Decode each picked Clip ONCE — on open, and again only for a clip picked later. This is
  // the one step that hits the short-TTL URL: the frames feed both the length stepper (the
  // longest loop) and the encode (handed in as `frames`, so Encode never re-fetches; #138).
  // Best-effort: a clip that fails is recorded as null (length unknown; the encode reports
  // it under "Tiled N of M") rather than retried on every render. `inFlight` keeps a reorder
  // mid-decode (a new `items` identity) from starting a second decode of the same clip.
  const inFlight = useRef(new Set<string>());
  useEffect(() => {
    for (const it of items) {
      if (!isClip(it.key) || clipFrames.has(it.key) || inFlight.current.has(it.key)) continue;
      inFlight.current.add(it.key);
      void decodeClip(it.url)
        .catch(() => null)
        .then((frames) => {
          inFlight.current.delete(it.key);
          setClipFrames((prev) => new Map(prev).set(it.key, frames));
        });
    }
  }, [items, clipFrames, decodeClip]);

  // The output length. Until every clip is decoded the longest loop is unknown ("probing"),
  // so the stepper reads "…" and Encode waits — a loop count before then is a guess.
  const durations = items.map((it) => {
    const frames = clipFrames.get(it.key);
    return frames ? clipDuration(frames) : 0;
  });
  const probing = items.some((it) => isClip(it.key) && !clipFrames.has(it.key));
  const baseSeconds = mosaicDuration(durations);
  const loopCap = maxLoops(baseSeconds);
  const effectiveLoops = Math.min(loopCap, Math.max(1, loops ?? loopsForTarget(baseSeconds)));
  const outputSeconds = baseSeconds * effectiveLoops;

  const encode = useCallback(async () => {
    const selection: MosaicSelectionItem[] = items.map((item) => ({
      url: item.url,
      isClip: isClip(item.key),
      frames: clipFrames.get(item.key) ?? undefined,
    }));
    const ac = new AbortController();
    abortRef.current = ac;
    setPhase("loading");
    setError(null);
    setPercent(0);
    setTiled(null);
    setObjectUrl(null);
    try {
      const blob = await buildMosaic(selection, {
        layout,
        cols: effectiveCols,
        loops: effectiveLoops,
        signal: ac.signal,
        onLoad: () => setPhase("encoding"),
        onProgress: (p) => {
          setPhase("encoding");
          setPercent(p);
        },
        onTiled: (n, of) => {
          if (n < of) setTiled({ n, of });
        },
      });
      setObjectUrl(URL.createObjectURL(blob));
      setPhase("ready");
    } catch (e) {
      if ((e as Error)?.name === "AbortError") {
        setPhase("idle"); // cancelled cleanly — back to the preview
      } else {
        setError(e instanceof Error ? e.message : "Mosaic failed.");
        setPhase("error");
      }
    } finally {
      abortRef.current = null;
    }
  }, [buildMosaic, layout, effectiveCols, effectiveLoops, clipFrames, items, setObjectUrl]);

  const cancelEncode = useCallback(() => abortRef.current?.abort(), []);

  // On completion, move focus to the Download link + announce — otherwise focus falls to
  // <body> when the Cancel button unmounts, and success is silent (WCAG 2.4.3 / 4.1.3).
  useEffect(() => {
    if (phase === "ready") {
      downloadRef.current?.focus();
      setLiveMsg("Mosaic ready — download below.");
    }
  }, [phase]);

  function moveCell(from: number, to: number): void {
    if (to < 0 || to >= selectionCount) return;
    onReorder?.(from, to);
    setLiveMsg(`Moved ${mediaLabel(items[from]!)} to position ${to + 1} of ${selectionCount}.`);
  }

  // Step the grid width. The number is the source of truth (presets are shortcuts to a
  // natural width); we announce the resulting shape — a stepper press is otherwise silent.
  function setColumns(next: number): void {
    const clamped = Math.min(maxCols, Math.max(1, next));
    setCols(clamped);
    const g = planGrid(maxCols, layout, clamped);
    setLiveMsg(`${g.cols} column${g.cols === 1 ? "" : "s"} — ${g.cols}×${g.rows} grid.`);
  }

  // Step the output length by one loop of the base (the longest clip, or the 3 s still) and
  // announce the result — a stepper press is otherwise silent to a screen reader.
  function setLength(nextLoops: number): void {
    const clamped = Math.min(loopCap, Math.max(1, nextLoops));
    setLoops(clamped);
    setLiveMsg(
      `${formatSeconds(baseSeconds * clamped)} — ${clamped} loop${clamped === 1 ? "" : "s"} of ${formatSeconds(baseSeconds)}.`,
    );
  }

  // Click a layout preset → jump to its natural width (clears any manual override).
  function pickLayout(value: MosaicLayout): void {
    setLayout(value);
    setCols(null);
  }

  return (
    <Modal
      label="Mosaic"
      onClose={onClose}
      canClose={() => !busy}
      closeDisabled={busy}
      testId="mosaic"
    >
      <div className="visually-hidden" aria-live="polite" data-testid="mosaic-live">
        {liveMsg}
      </div>

      {phase === "error" ? (
        <p className="sheet-overlay__msg sheet-overlay__msg--error" role="alert">
          {error}
        </p>
      ) : phase === "loading" ? (
        <p className="sheet-overlay__msg" role="status">
          Reading your selection…
        </p>
      ) : phase === "encoding" ? (
        // A determinate progressbar (NOT a polite live region rewritten every tick — that
        // re-speaks the whole string continuously); SRs surface a progressbar on demand.
        <div
          className="sheet-overlay__msg"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuetext={`Encoding ${percent}%`}
        >
          Encoding {percent}%…
        </div>
      ) : phase === "ready" && mp4Url ? (
        <>
          <video
            className="sheet-overlay__img"
            data-testid="mosaic-preview"
            src={mp4Url}
            controls
            loop
            muted
            playsInline
            autoPlay
          />
          {tiled && (
            <p className="sheet-overlay__msg" role="status" data-testid="mosaic-tiled">
              Tiled {tiled.n} of {tiled.of} — {tiled.of - tiled.n} couldn’t be read.
            </p>
          )}
        </>
      ) : selectionCount === 0 ? (
        <p className="sheet-overlay__msg" role="status">
          Nothing selected.
        </p>
      ) : (
        // Idle: the live, reorderable grid preview — the cells you can arrange, plus the
        // greyed loop-fill tiles the encoder repeats to fill the last partial row, so the
        // preview matches the produced MP4 (planGrid loops cells[i % count]).
        <>
          <ul
            className="mosaic-preview"
            style={
              {
                gridTemplateColumns: `repeat(${grid.cols}, 1fr)`,
                // The CSS fit-to-58vh width formula needs the grid shape (see .mosaic-preview).
                "--mcols": grid.cols,
                "--mrows": grid.rows,
              } as React.CSSProperties
            }
            aria-label="Mosaic cells — drag, or focus a cell and use arrow keys, to reorder"
          >
            {Array.from({ length: slotCount }, (_, i) => {
              const item = items[i % selectionCount]!;
              if (i < selectionCount) {
                return (
                  <li key={item.key} className="mosaic-preview__slot">
                    <button
                      type="button"
                      draggable
                      data-testid="mosaic-cell"
                      className={"mosaic-cell" + (dragIndex === i ? " is-dragging" : "")}
                      aria-label={`Cell ${i + 1} of ${selectionCount}: ${mediaLabel(item)} — arrow keys to move`}
                      onDragStart={() => setDragIndex(i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        if (dragIndex != null) moveCell(dragIndex, i);
                        setDragIndex(null);
                      }}
                      onDragEnd={() => setDragIndex(null)}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                          e.preventDefault();
                          moveCell(i, i - 1);
                        } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                          e.preventDefault();
                          moveCell(i, i + 1);
                        }
                      }}
                    >
                      <img src={item.url} alt="" crossOrigin="anonymous" />
                      <span className="mosaic-cell__num" aria-hidden="true">
                        {i + 1}
                      </span>
                    </button>
                  </li>
                );
              }
              // Loop-fill tile (the encoder repeats from the start): shown, dimmed, inert.
              return (
                <li key={`loop-${i}`} className="mosaic-preview__slot">
                  <div className="mosaic-cell mosaic-cell--loop" aria-hidden="true">
                    <img src={item.url} alt="" crossOrigin="anonymous" />
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="mosaic-dims" data-testid="mosaic-dims">
            {grid.cols}×{grid.rows} grid · {grid.width}×{grid.height}px
            {slotCount > selectionCount ? " · last tiles repeat to fill the grid" : ""}
            {probing ? " · reading clips…" : ""}
          </p>
        </>
      )}

      {/* The layout + column controls disappear once an MP4 is ready (it's encoded for one grid). */}
      {phase !== "ready" && (
        <div className="sheet-overlay__ctl">
          <div className="sheet-overlay__group" role="group" aria-label="Layout">
            {LAYOUTS.map((o) => {
              // A preset reads as active when the current width matches its natural one — so
              // stepping away from a preset de-highlights it, and stepping back re-lights it.
              const on = planGrid(maxCols, o.value).cols === effectiveCols;
              return (
                <button
                  key={o.value}
                  type="button"
                  className={"sheet-overlay__opt" + (on ? " is-on" : "")}
                  aria-pressed={on}
                  disabled={busy}
                  onClick={() => pickLayout(o.value)}
                >
                  {o.label}
                </button>
              );
            })}
          </div>

          {/* Column stepper — the explicit grid-width control (e.g. 12 cells → 3 cols → 3×4).
             Hidden when there's nothing to arrange (≤ 1 cell has no column choice). */}
          {maxCols > 1 && (
            <div className="sheet-overlay__group mosaic-step" role="group" aria-label="Columns">
              <button
                type="button"
                className="sheet-overlay__opt mosaic-step__btn"
                aria-label="Fewer columns"
                disabled={busy || effectiveCols <= 1}
                onClick={() => setColumns(effectiveCols - 1)}
              >
                −
              </button>
              <span className="mosaic-step__val" data-testid="mosaic-cols">
                {effectiveCols} col{effectiveCols === 1 ? "" : "s"}
              </span>
              <button
                type="button"
                className="sheet-overlay__opt mosaic-step__btn"
                aria-label="More columns"
                disabled={busy || effectiveCols >= maxCols}
                onClick={() => setColumns(effectiveCols + 1)}
              >
                +
              </button>
            </div>
          )}

          {/* Length stepper — whole loops of the longest clip (or of the 3 s still), defaulting
             to the count nearest 30 s. It steps in loops so the longest clip always ends on a
             boundary, and reads in seconds (what a post needs). "…" until every clip is decoded. */}
          {selectionCount > 0 && (
            <div className="sheet-overlay__group mosaic-step" role="group" aria-label="Length">
              <button
                type="button"
                className="sheet-overlay__opt mosaic-step__btn"
                aria-label="Shorter"
                disabled={busy || probing || effectiveLoops <= 1}
                onClick={() => setLength(effectiveLoops - 1)}
              >
                −
              </button>
              <span className="mosaic-step__val" data-testid="mosaic-secs">
                {probing ? "…" : formatSeconds(outputSeconds)}
              </span>
              <button
                type="button"
                className="sheet-overlay__opt mosaic-step__btn"
                aria-label="Longer"
                disabled={busy || probing || effectiveLoops >= loopCap}
                onClick={() => setLength(effectiveLoops + 1)}
              >
                +
              </button>
            </div>
          )}
        </div>
      )}

      {phase === "ready" && mp4Url ? (
        <a
          ref={downloadRef}
          className="sheet-overlay__dl"
          href={mp4Url}
          download={uniqueDownloadName(mosaicFilename(grid))}
          data-testid="mosaic-download"
        >
          Download MP4
        </a>
      ) : busy ? (
        <button
          type="button"
          className="sheet-overlay__dl"
          onClick={cancelEncode}
          data-testid="mosaic-cancel"
        >
          Cancel
        </button>
      ) : (
        <button
          type="button"
          className="sheet-overlay__dl"
          onClick={encode}
          disabled={selectionCount === 0 || probing}
          data-testid="mosaic-encode"
        >
          Encode MP4
        </button>
      )}
    </Modal>
  );
}
